import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LANGUAGES, ensureLanguage } from '../i18n';

// Globe dropdown for switching UI language. Sits in the header next to the theme
// toggle. The choice is persisted to localStorage by the i18next LanguageDetector.
export default function LanguageSwitcher({ variant = 'header' }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onDocClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const current = (i18n.language || 'en').split('-')[0];
  const currentLang = LANGUAGES.find((l) => l.code === current) || LANGUAGES[0];

  function choose(code) {
    // Fetch the strings first: the locales are separate chunks now, and
    // switching before they land would repaint in English and then again in
    // the chosen language.
    ensureLanguage(code).then(() => i18n.changeLanguage(code));
    setOpen(false);
  }

  const Globe = (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="12" r="9" />
      <path strokeLinecap="round" d="M3 12h18M12 3c2.5 2.5 2.5 16 0 18M12 3c-2.5 2.5-2.5 16 0 18" />
    </svg>
  );

  // Mobile menu variant — a full-width row mirroring the theme row.
  if (variant === 'menu') {
    return (
      <div className="relative" ref={ref}>
        <button
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-white/[0.06] transition-colors"
        >
          <span className="flex items-center gap-2">{Globe}{t('language.label')}</span>
          <span className="text-xs text-gray-500">{currentLang.native}</span>
        </button>
        {open && (
          <div className="mt-1 mx-1 rounded-xl py-1.5 shadow-2xl" style={{ background: 'var(--lx-surface)', border: '1px solid var(--lx-border)' }}>
            {LANGUAGES.map((l) => (
              <button key={l.code} onClick={() => choose(l.code)}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg mx-1 ${l.code === current ? 'text-white bg-white/[0.06]' : 'text-gray-400 hover:text-white'}`}>
                <span>{l.native}</span>
                {l.code === current && <span className="text-indigo-400">✓</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Header variant — icon button + dropdown.
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        title={t('language.choose')}
        aria-label={t('language.choose')}
        className="h-8 flex items-center gap-1.5 px-2.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/[0.06] transition-colors"
      >
        {Globe}
        <span className="text-sm font-medium">{currentLang.native}</span>
        <svg className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 mt-1.5 w-44 rounded-xl py-1.5 z-50 shadow-2xl shadow-black/50"
          style={{ background: 'var(--lx-surface)', border: '1px solid var(--lx-border)' }}>
          <p className="px-3 pt-1 pb-1.5 text-[10px] uppercase tracking-wider text-gray-600 font-semibold">{t('language.label')}</p>
          {LANGUAGES.map((l) => (
            <button key={l.code} onClick={() => choose(l.code)}
              className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg mx-1 ${l.code === current ? 'text-white bg-white/[0.06]' : 'text-gray-400 hover:text-white'}`}
              style={{ width: 'calc(100% - 8px)' }}>
              <span>{l.native}</span>
              {l.code === current && <span className="text-indigo-400 text-xs">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
